/**
 * G2eGente - Funcionário Responde Diagnóstico NR01
 * Interface móvel para resposta anônima - 35 perguntas
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { nr01API } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft,
  ShieldCheck,
  Loader2,
  CheckCircle2,
  Shield,
  ChevronRight,
  ChevronLeft,
  Send
} from 'lucide-react';
import { toast } from 'sonner';

const SCALE_OPTIONS = [
  { value: 1, label: 'Nunca' },
  { value: 2, label: 'Raramente' },
  { value: 3, label: 'Às vezes' },
  { value: 4, label: 'Frequentemente' },
  { value: 5, label: 'Sempre' }
];

export default function EmployeeNR01Diagnostic() {
  const navigate = useNavigate();
  const { diagnosticId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [diagnostic, setDiagnostic] = useState(null);
  const [currentDimensao, setCurrentDimensao] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    // Check if already answered (localStorage)
    const answeredDiagnostics = JSON.parse(localStorage.getItem('answeredNR01Diagnostics') || '[]');
    if (answeredDiagnostics.includes(diagnosticId)) {
      setSubmitted(true);
      setLoading(false);
      return;
    }
    
    fetchDiagnostic();
  }, [diagnosticId]);

  const fetchDiagnostic = async () => {
    try {
      const { data } = await nr01API.employeeGetDiagnostic(diagnosticId);
      setDiagnostic(data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Seu setor não está incluído neste diagnóstico');
      } else {
        toast.error('Erro ao carregar diagnóstico');
      }
      navigate('/m/employee');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (perguntaId, valor) => {
    setAnswers(prev => ({
      ...prev,
      [perguntaId]: valor
    }));
  };

  // Group questions by dimension
  const getQuestionsByDimension = () => {
    if (!diagnostic?.perguntas || !diagnostic?.dimensoes) return [];
    
    return diagnostic.dimensoes.map(dim => ({
      ...dim,
      perguntas: diagnostic.perguntas.filter(p => p.dimensao === dim.id)
    }));
  };

  const dimensoesComPerguntas = getQuestionsByDimension();
  const currentDim = dimensoesComPerguntas[currentDimensao];
  const totalDimensoes = dimensoesComPerguntas.length;
  
  // Calculate overall progress
  const totalPerguntas = diagnostic?.perguntas?.length || 35;
  const perguntasRespondidas = Object.keys(answers).length;
  const progressPercent = Math.round((perguntasRespondidas / totalPerguntas) * 100);

  // Check if current dimension is complete
  const isDimensaoComplete = () => {
    if (!currentDim) return false;
    return currentDim.perguntas.every(p => answers[p.id] !== undefined);
  };

  const handleNext = () => {
    if (!isDimensaoComplete()) {
      toast.error('Por favor, responda todas as perguntas desta seção');
      return;
    }
    
    if (currentDimensao < totalDimensoes - 1) {
      setCurrentDimensao(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    if (currentDimensao > 0) {
      setCurrentDimensao(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    // Validate all questions
    const unanswered = diagnostic.perguntas.filter(p => answers[p.id] === undefined);
    if (unanswered.length > 0) {
      toast.error(`Faltam ${unanswered.length} perguntas para responder`);
      return;
    }

    setSubmitting(true);
    try {
      const respostas = Object.entries(answers).map(([pergunta_id, valor]) => ({
        pergunta_id,
        valor
      }));

      await nr01API.employeeSubmit(diagnosticId, {
        respostas,
        setor: diagnostic.employee_setor
      });

      // Mark as answered in localStorage
      const answeredDiagnostics = JSON.parse(localStorage.getItem('answeredNR01Diagnostics') || '[]');
      answeredDiagnostics.push(diagnosticId);
      localStorage.setItem('answeredNR01Diagnostics', JSON.stringify(answeredDiagnostics));

      setSubmitted(true);
      toast.success('Resposta enviada com sucesso!');
    } catch (error) {
      toast.error('Erro ao enviar resposta');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  // Already submitted
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-amber-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-6">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Obrigado!</h2>
            <p className="text-slate-600 mb-6">
              Sua participação é fundamental para melhorarmos o ambiente de trabalho. 
              Suas respostas são anônimas e confidenciais.
            </p>
            <Button onClick={() => navigate('/m/employee')} className="bg-amber-600 hover:bg-amber-700">
              Voltar ao Início
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!diagnostic || !currentDim) return null;

  const isLastDimension = currentDimensao === totalDimensoes - 1;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/m/employee')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="font-semibold text-slate-800 line-clamp-1">{diagnostic.titulo}</h1>
              <p className="text-xs text-slate-500">
                Seção {currentDimensao + 1} de {totalDimensoes} • {progressPercent}% concluído
              </p>
            </div>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>
      </div>

      {/* Confidentiality Notice (first dimension only) */}
      {currentDimensao === 0 && (
        <div className="mx-4 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2 text-amber-700">
            <Shield className="w-4 h-4 flex-shrink-0" />
            <p className="text-sm">
              <strong>100% anônimo</strong> - Suas respostas não podem ser identificadas. 
              Avalie o <strong>ambiente do seu setor</strong>, não pessoas específicas.
            </p>
          </div>
        </div>
      )}

      {/* Dimension Header */}
      <div className="p-4">
        <Card className="mb-4 bg-amber-50 border-amber-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <Badge className="mb-2 bg-amber-100 text-amber-700">
                  {currentDim.nome}
                </Badge>
                <p className="text-sm text-amber-800">{currentDim.descricao}</p>
              </div>
              <span className="text-2xl font-bold text-amber-600">
                {currentDim.perguntas.filter(p => answers[p.id]).length}/{currentDim.perguntas.length}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Questions */}
        <div className="space-y-4">
          {currentDim.perguntas.map((pergunta, idx) => (
            <Card key={pergunta.id} className={answers[pergunta.id] ? 'border-amber-300 bg-amber-50/30' : ''}>
              <CardContent className="pt-4 pb-4">
                <p className="text-slate-800 mb-4">
                  <span className="text-amber-600 font-semibold">{idx + 1}.</span> {pergunta.texto}
                </p>
                
                <div className="space-y-2">
                  {SCALE_OPTIONS.map(opt => (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        answers[pergunta.id] === opt.value
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-slate-200 hover:border-amber-300'
                      }`}
                      onClick={() => handleAnswer(pergunta.id, opt.value)}
                    >
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        answers[pergunta.id] === opt.value
                          ? 'border-amber-500 bg-amber-500'
                          : 'border-slate-300'
                      }`}>
                        {answers[pergunta.id] === opt.value && (
                          <div className="w-2 h-2 rounded-full bg-white" />
                        )}
                      </div>
                      <span className={`flex-1 text-sm ${
                        answers[pergunta.id] === opt.value ? 'text-amber-700 font-medium' : 'text-slate-600'
                      }`}>
                        {opt.label}
                      </span>
                      <span className={`text-lg font-bold ${
                        answers[pergunta.id] === opt.value ? 'text-amber-600' : 'text-slate-300'
                      }`}>
                        {opt.value}
                      </span>
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex gap-3 mt-6 mb-8">
          {currentDimensao > 0 && (
            <Button variant="outline" onClick={handlePrevious} className="flex-1">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Anterior
            </Button>
          )}
          
          {!isLastDimension ? (
            <Button 
              onClick={handleNext} 
              className="flex-1 bg-amber-600 hover:bg-amber-700"
              disabled={!isDimensaoComplete()}
            >
              Próxima Seção
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit} 
              disabled={submitting || !isDimensaoComplete()}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Enviar Respostas
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
